// How the preview keeps its media at the clock while it plays (spec playback-trail): at every
// animation frame, each video layer's and sound's element is told what to do at that frame's time,
// from the project alone. A drift is closed by playing a little faster or slower, and moved over
// (a seek) only when it is too far to catch up. A video asked to play shows its first new picture
// only about 100 ms later (its sound's output starts first, and unmuting starts it again), by then
// some way into its file: so a video loaded ahead waits that far before its in-point and starts
// that long before its cut, at volume 0, not muted, and its first picture is its in-point's, at
// its cut. Pure: the preview reads each element and applies the move (`Preview.tsx`), so the rules
// are tested without a browser.
import { fileMsAt, speedAtMs } from "../speed-curve";
import type { Layer, Sound } from "./project";
import { gainAt, showsAt, sourceMsAt, within } from "./schedule";
import { MAX_SPEED, MIN_SPEED } from "./stretch";

/** What an element reports now. */
export interface MediaNow {
  /** Its time, ms of its own file. */
  currentMs: number;
  paused: boolean;
  /** Its playback rate. */
  rate: number;
  /** A seek it was given is still landing: its time is not moved again meanwhile. */
  seeking: boolean;
  muted: boolean;
  volume: number;
  /** What its sound is boosted by past its own volume of 1 (`boost.ts`, spec volume-boost): 1, or none, when it is not. */
  boost?: number;
  /**
   * How long it has played with no progress (ms): its time, its ready state and what it has of its
   * file all still, as when a read of its file never came back.
   */
  stalledMs: number;
  /** How many times it was loaded again in this play (spec stalled-element). */
  reloads: number;
}

/**
 * What it should do at t: play there in its file, or rest there; heard at `gain`, or muted (null).
 * `early`: playing ahead of its clip, from its in-point, so it moves when its cut comes: at its
 * own speed, never moved, and at volume 0 when its clip has sound, so that sound's output has
 * started by the cut (muting would start it only then).
 */
export interface MediaWant {
  plays: boolean;
  atMs: number;
  gain: number | null;
  early?: boolean;
  /**
   * Its clip's speed (spec clip-speed), or its curve's at that frame (spec clip-speed-curves): the
   * rate it plays at once caught up, 1 when left out.
   */
  speed?: number;
}

/** The changes to make: a field is left out where nothing changes. */
export interface MediaMove {
  /** Load its file again, before the rest: a read that never came back holds an element. */
  reload?: true;
  play?: true;
  pause?: true;
  seekMs?: number;
  rate?: number;
  muted?: boolean;
  volume?: number;
  /** What to boost its sound by past its own volume of 1 (`boost.ts`). */
  boost?: number;
}

/** Playing within this of the clock, an element plays at its own speed (a frame at 30 fps is 33 ms). */
export const IN_STEP_MS = 15;
/** Playing further off, it plays at 1 + drift / CATCH_UP_MS: 50 ms behind plays at 1.2. */
export const CATCH_UP_MS = 250;
/** The fastest and slowest it is asked to play, so a change of tempo is heard as little as can be. */
export const MOST_RATE = 1.25;
export const LEAST_RATE = 0.8;
/** Rates are asked in these steps: a drift that hardly moves does not ask again every frame. */
export const RATE_STEP = 0.05;
/** Playing further off than this, it is moved to its time: catching up would take seconds. */
export const JUMP_MS = 400;
/** Resting further off than this from its frame, it is moved there. */
export const REST_DRIFT_MS = 40;
/**
 * Playing with no progress for this long, it is loaded again, moved to its time and started (spec
 * stalled-element): a read of its file that never came back holds its seek, and the browser waits on
 * that read for any seek near it, so only loading again reads anew. Each time again in a play waits
 * twice as long, at most `MOST_STALLED_MS`. On dev a clip waits up to 1.4 s for its data at its cut,
 * so a shorter wait loads again one whose read is coming (spec scrub-priority).
 */
export const STALLED_MS = 2000;
export const MOST_STALLED_MS = 4000;
/**
 * How a video starts in this browser: from asking it to play to its first new picture (`leadMs`),
 * and how far into its file that picture is from where it was asked (`aheadMs`).
 */
export interface StartPace {
  leadMs: number;
  aheadMs: number;
}

/** The pace expected before a start has been seen: about this Mac's in Chrome (spec playback-trail). */
export const FIRST_PACE: StartPace = { leadMs: 110, aheadMs: 60 };
/** The longest start learned from: a stalled file is not a start. */
export const LONGEST_START_MS = 400;

/** The pace to expect next, after a start just seen: a running mean that follows the browser's own. */
export function nextPace(pace: StartPace, seen: StartPace): StartPace {
  const toward = (now: number, value: number) =>
    Math.round(0.7 * now + 0.3 * Math.min(LONGEST_START_MS, Math.max(0, value)));
  return { leadMs: toward(pace.leadMs, seen.leadMs), aheadMs: toward(pace.aheadMs, seen.aheadMs) };
}

/** The rate that closes `driftMs` (positive: the element is behind), in steps, within the bounds. */
export function catchUpRate(driftMs: number): number {
  if (Math.abs(driftMs) <= IN_STEP_MS) return 1;
  const rate = Math.min(MOST_RATE, Math.max(LEAST_RATE, 1 + driftMs / CATCH_UP_MS));
  return Number((Math.round(rate / RATE_STEP) * RATE_STEP).toFixed(2));
}

/** What to change on an element so it does what `want` asks at this frame. */
export function moveToward(now: MediaNow, want: MediaWant): MediaMove {
  const move: MediaMove = {};
  const drift = want.atMs - now.currentMs;
  // A clip's speed (spec clip-speed) times the factor that closes the drift, within what plays.
  const speed = want.speed ?? 1;
  const paced = (factor: number) => Math.min(MAX_SPEED, Math.max(MIN_SPEED, speed * factor));
  let rate = speed;
  if (want.early) {
    // Started where it waits; one not there yet is moved there first (once moving, it is left).
    if (now.paused && !now.seeking && Math.abs(drift) <= REST_DRIFT_MS) move.play = true;
    else if (now.paused && !now.seeking) move.seekMs = want.atMs;
  } else if (want.plays) {
    if (now.paused) move.play = true;
    if (!now.paused && now.stalledMs > Math.min(MOST_STALLED_MS, STALLED_MS * 2 ** now.reloads)) {
      move.reload = true;
      move.seekMs = want.atMs;
      move.play = true;
    } else if (!now.seeking && Math.abs(drift) > JUMP_MS) move.seekMs = want.atMs;
    else if (!now.seeking) {
      // A drift its rate cannot close, held to what plays (a 4x clip behind, a 0.25x one ahead), is
      // moved to its time instead (spec clip-speed).
      const factor = catchUpRate(drift / speed);
      if (factor !== 1 && paced(factor) === speed) move.seekMs = want.atMs;
      else rate = paced(factor);
    }
    else rate = now.rate;
  } else {
    if (!now.paused) move.pause = true;
    if (!now.seeking && Math.abs(drift) > REST_DRIFT_MS) move.seekMs = want.atMs;
  }
  if (rate !== now.rate) move.rate = rate;
  const muted = want.gain === null;
  if (muted !== now.muted) move.muted = muted;
  if (want.gain !== null) {
    // An element plays at most at its own volume of 1; a level past it (spec volume-boost) is
    // boosted by the rest.
    const level = Math.max(0, want.gain);
    const volume = Math.min(1, level);
    if (Math.abs(volume - now.volume) > 0.005) move.volume = volume;
    const boost = Math.max(1, level);
    if (Math.abs(boost - (now.boost ?? 1)) > 0.005) move.boost = boost;
  }
  return move;
}

/**
 * What a video layer's element should do at t: play in its own span, heard at its sound's level
 * there (muted with none); rest at its last frame after it. Before it, one a transition holds on
 * its first frame rests there, before its hold too, so the hold finds that frame; any other waits
 * a start's advance before its in-point, and starts a start's lead before its cut.
 */
export function layerWant(layer: Layer, sound: Sound | undefined, atMs: number, pace: StartPace): MediaWant {
  const plays = within(layer, atMs);
  const coming = layer.shownFromMs >= layer.startMs && !showsAt(layer, atMs) && atMs < layer.startMs;
  const early = coming && layer.startMs - atMs <= pace.leadMs;
  const heard = sound === undefined ? null : plays ? gainAt(sound, atMs) : early ? 0 : null;
  // A curve's speed where it is: at its start while it waits for its cut.
  const speed = speedAtMs(layer, coming ? layer.startMs : atMs);
  const at = coming ? Math.max(0, layer.inMs - pace.aheadMs * speed) : sourceMsAt(layer, atMs);
  const paced = speed === 1 ? {} : { speed };
  return { plays: plays || early, atMs: at, gain: heard, ...paced, ...(early ? { early } : {}) };
}

/** What a sound's element should do at t: play in its span at its level then, rest outside it. */
export function soundWant(sound: Sound, atMs: number): MediaWant {
  const plays = within(sound, atMs);
  const at = Math.min(Math.max(atMs, sound.startMs), sound.endMs);
  const atMsOfFile = fileMsAt(sound, at);
  const speed = speedAtMs(sound, at);
  const paced = speed === 1 ? {} : { speed };
  return { plays, atMs: atMsOfFile, gain: plays ? gainAt(sound, atMs) : null, ...paced };
}
